/**
 * Supported funnel locales and their formatting metadata.
 *
 * The i18next language code doubles as the URL path segment (e.g. /jp, /es).
 * `intlLocale` is the BCP-47 tag used for Intl/`toLocaleDateString` formatting.
 *
 * Adding a language = add an entry here, a `<lang>.json` in src/locales, and
 * (for lazy-loaded langs) a loader in src/i18n.ts. English is the default and
 * is always bundled; other languages may be lazy-loaded.
 */
export type AppLocale = 'en' | 'es' | 'jp';

export interface LocaleConfig {
    code: AppLocale;
    /** BCP-47 locale for Intl / toLocaleDateString */
    intlLocale: string;
    /** value for <html lang> */
    htmlLang: string;
    label: string;
}

export const LOCALES: Record<AppLocale, LocaleConfig> = {
    en: { code: 'en', intlLocale: 'en-US', htmlLang: 'en', label: 'English' },
    es: { code: 'es', intlLocale: 'es-MX', htmlLang: 'es', label: 'Español' },
    jp: { code: 'jp', intlLocale: 'ja-JP', htmlLang: 'ja', label: '日本語' },
};

export const SUPPORTED_LOCALES = Object.keys(LOCALES) as AppLocale[];

export const DEFAULT_LOCALE: AppLocale = 'en';

export function isSupportedLocale(value: string | undefined | null): value is AppLocale {
    return !!value && (SUPPORTED_LOCALES as string[]).includes(value);
}

/** Resolve the BCP-47 Intl locale for an i18next language code, falling back to English. */
export function intlLocaleFor(language: string): string {
    return isSupportedLocale(language) ? LOCALES[language].intlLocale : LOCALES.en.intlLocale;
}
